<!--
  INFO:
    Stepper displays a vertical list of numbered navigation steps, always with a current
    step highlighted. It is designed to be used with other paged UI content.

    Steps become interactive if the @select event is bound. Each step can be disabled
    individually if necessary. Steps can also be displayed in a green 'complete' state,
    or in a red 'invalid' state.

    Font family and size will be inherited from the page. There are no dependencies other
    than Vue and SCSS.

  PROPS:
    currentStep: Number of currently selected step (starting at 1 not 0)

    steps: Array of step names and optional status values (complete/invalid/disabled), e.g.
      [
        { name: 'Example step 1...' },
        { name: 'Example step 2...', status: 'complete' },
        { name: 'Example step 3...', status: 'invalid' },
        { name: 'Example step 4...', status: 'disabled' },
      ]

    stepSpacing: (Optional) Vertical spacing between steps, e.g "10px" or "1.5em" etc. Default is "1em"

  EVENTS:
    @select(stepNumber) - Makes steps clickable and returns the clicked step number. This will not actually change the current step because it is controlled by the parent component (through the currentStep property).

-->

<template>
  <nav
    class="ads-stepper"
    role="navigation"
  >
    <transition-group
      tag="ol"
      name="stepper"
    >
      <li
        v-for="(step, idx) in steps"
        :key="step.name"
        :style="idx + 1 !== steps.length && {'margin-bottom': stepSpacing}"
        class="stepper-item"
      >
        <button
          :class="getStepCssClasses(idx)"
          type="button"
          @click="selectStep(idx)"
          :aria-expanded="isSelected(idx)"
          :aria-label="stepAriaLabel(idx, step)"
          :disabled="!isInteractive || step.status === 'disabled'"
        >
          <span class="step-circle">
            <svg
              v-if="step.status === 'complete'"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="14"
              viewBox="0 0 18 14"
              fill="none"
            >
              <path
                d="M5.79508 10.875L1.62508 6.705L0.205078 8.115L5.79508 13.705L17.7951 1.705L16.3851 0.294998L5.79508 10.875Z"
                fill="white"
              />
            </svg>
            <svg
              v-else-if="step.status === 'invalid'"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              width="4"
              height="18"
              viewBox="0 0 4 18"
              fill="none"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M4 0H0V12H4V0ZM0 16C0 14.8954 0.89543 14 2 14C3.10457 14 4 14.8954 4 16C4 17.1046 3.10457 18 2 18C0.89543 18 0 17.1046 0 16Z"
                fill="#B81237"
              />
            </svg>
            <span v-else>{{ idx + 1 }}</span>
          </span>
          <span class="step-name">{{ step.name }}</span>
        </button>
      </li>
    </transition-group>
  </nav>
</template>

<script>
export default {
  name: 'Stepper',
  props: {
    steps: {
      default() {
        return [{name: 'Example step 1...'}]
      },
      type: Array
    },
    currentStep: {
      default() {
        return 1
      },
      type: Number
    },
    stepSpacing: {
      default() {
        return '1em'
      },
      type: String
    }
  },
  computed: {
    isInteractive() {
      // If a @select event has been bound, make the control interactive
      return this.$listeners.select ? true : false
    }
  },

  methods: {
    stepAriaLabel(stepIdx, step) {
      const validStatuses = ['complete', 'invalid', 'disabled'];
      const statusDesc = validStatuses.indexOf((step.status || '').toLowerCase()) !== -1 ? ` (${step.status})` : '';
      return `Step ${stepIdx + 1} of ${this.steps.length}${statusDesc}. ${step.name}.`;
    },
    isSelected(stepIdx) {
      return stepIdx === this.currentStep - 1
    },
    getStepCssClasses(stepIdx) {
      let classes = ['step']
      let step = this.steps[stepIdx]
      if (this.isSelected(stepIdx)) {
        classes.push('selected')
      }
      if (step.status) {
        classes.push(step.status)
      }
      return classes.join(' ')
    },
    selectStep(stepIdx) {
      if (!this.isSelected(stepIdx)) {
        this.$emit('select', stepIdx + 1)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables.scss';

$border-width: 0.15em; // Used for circle border width and connecting line widths
$font-size: 1.25em;

.ads-stepper {
  color: $ads-navy;
  white-space: nowrap;
  line-height: 1;
}

.ads-stepper ol {
  position: relative;
  list-style: none;
  padding: $border-width * 2; // Makes room for the selected step which expands slightly into the space around it

  &::before {
    font-size: $font-size;
    position: absolute;
    content: "";
    height: calc(100% - #{$border-width * 4}); // Distance from top of first step circle to bottom of last step circle
    top: $border-width * 2;
    left: 1.315em; // Center of circle (ol padding corrected for font size (0.24em) + circle border (0.15em) + half circle width (1em) - half border width (0.075em)
    border-left: $border-width solid;
  }
}

.step {
  display: flex;
  align-items: center;
  opacity: 1;
  text-align: left;
  width: 100%;
  font-weight: bold;
  overflow: hidden;
  border-radius: 2em;

  // Remove default button styles (as each step is a button)
  background-color: transparent;
  padding: 0;
  margin: 0;
  border: 0;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  line-height: 1;

  padding: $border-width * 2; // Used to slightly expand selected step into the space around it
  margin: -$border-width * 2; // Used to counteract above padding, so other elements do not shift

  position: relative;
  z-index: 1;

  &.selected {
    background-color: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  }

  &:focus {
    outline: none; // Hides default focus ring
    box-shadow: 0 0 0 $border-width $ads-blue-2;
  }

  &:disabled {
    cursor: default;
  }
}

.step-name {
  white-space: normal;
  margin: $border-width $border-width $border-width 0;
}

.step-circle {
  border: $border-width solid $ads-navy;
  border-radius: 50%;
  display: inline-block;
  background-color: white;
  margin-right: 0.5em;
  font-size: $font-size;
  text-align: center;

  svg {
    padding: 0.5em;
  }
}

.selected .step-circle {
  color: white;
  background-color: $ads-navy;
}

.complete .step-circle {
  color: white;
  background-color: #30b400;
  border-color: #30b400;
}

.invalid .step-circle {
  color: #cd0337;
  border-color: #cd0337;
  background-color: white;
}

.step-circle > * {
  display: inline-block;
  width: 2em;
  height: 2em;
  line-height: 2em !important;
  font-weight: bold;
  vertical-align: middle;
}

.step-circle,
.step-name {
  vertical-align: middle;
  display: inline-block;
}

.theme--dark {
  .step-name {
    color: $ads-light-10 !important;
  }

  ol::after {
    border-color: $ads-light-10 !important;
  }

  .step {
    border-color: none;
  }

  .step:focus {
    box-shadow: 0 0 0 $border-width $ads-navy-dark !important;
  }

  .step.selected {
    background-color: $ads-24dp;
  }

  .invalid .step-circle {
    border-color: #cd0337;
  }

  .step-circle {
    background-color: $ads-dark !important;
    border: $border-width solid $ads-navy-dark !important;
    color: $ads-navy-dark !important;
  }

  .complete .step-circle {
    background-color: #30b400 !important;
    border: none;
  }

  .disabled .step-circle {
    background-color: $ads-dark-80 !important;
    border: 1px solid $ads-dark-80 !important;
  }

  .step.selected:not(.complete):not(.invalid) {
    .step-circle {
      background-color: $ads-navy-dark !important;
      color: $ads-dark !important;
    }
  }
}

// Transition classes
.stepper-item {
  transition: all 0.3s;
}

.stepper-leave-to {
  opacity: 0;
}

.stepper-leave-active {
  position: absolute;
}
</style>
